export const OFFER_TEMPLATE_IDS = [
    'classic',
    'tab',
    'unit',
    'columns',
    'compact',
];
/** The chip's defaults — what an absent `productBadges` key means, in one place. */
export const DEFAULT_PRODUCT_BADGES = {
    // OFF: shipping a feature is not the seller asking for it.
    enabled: false,
    position: 'top right',
    radius: 4,
    // Blank = "use the translated label", NOT "no label". See ThemeSettings.productBadges.
    soldOutText: '',
    soldOutBg: '#121212',
    soldOutFg: '#ffffff',
    highDemandText: '',
    highDemandBg: '#b3261e',
    highDemandFg: '#ffffff',
};
/** The Poppins stack shipped by theme.css (self-hosted @font-face). */
const POPPINS_STACK = "'Poppins', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
/**
 * The canonical v2 default = the Shrine "current" values from theme.css. The migrator normalizes ALL
 * stores to these (0019 Q3), and it is the no-theme fallback for the builder load + storefront serve.
 */
export const DEFAULT_THEME_SETTINGS = {
    presetId: 'default',
    schemaVersion: 2,
    colors: {
        accent1: '#2f5f8a',
        accent1Gradient: '',
        accent2: '#d2ebff',
        accent2Gradient: '',
        solidButtonLabel: '#ffffff',
        text: '#121212',
        outlineButton: '#000000',
        background1: '#ffffff',
        background1Gradient: '',
        background2: '#f3f3f3',
        background2Gradient: '',
    },
    typography: {
        heading: {
            font: POPPINS_STACK,
            lineHeight: 1.3,
            letterSpacing: 0.6,
            weight: 600,
            // 1.0 = the CURRENT rendered sizes. The --aqx-theme-text-* tokens are already the final Shrine px
            // (rem×10), so the neutral scale is 1.0; a 1.3 default would double-scale and inflate every heading
            // 30% (pixel-preservation, 0019 finding #10 area). The seller's font-size control scales from here.
            scale: 1.0,
        },
        body: {
            font: POPPINS_STACK,
            lineHeight: 1.8,
            letterSpacing: 0.6,
            weight: 400,
            scale: 1.0,
        },
    },
    shape: { radius: 'soft', containerWidth: 1400, spacing: 1, mediaRadius: 0 },
    // No favicon by default — a brand-new store has nothing to show, and an empty string is what the
    // backend stores for "none". The storefront emits the <link> only for a non-empty value.
    brand: { favicon: '' },
    // Urgency chips off until the seller turns them on in Theme settings → Product badges.
    productBadges: { ...DEFAULT_PRODUCT_BADGES },
};
/**
 * The SOLE theme migrator (0019 Q5): the front normalizes a stored blob to v2 on read; the backend never
 * up-converts (it only validates/clamps what the front writes). Idempotent (finding #7): a v2 blob is
 * returned untouched. A v1 (or unknown) blob is NORMALIZED to the Shrine defaults — Q3 does NOT
 * field-preserve the v1 indigo/cream palette or Plus-Jakarta/Inter fonts; only the structural `shape`
 * (e.g. a customized containerWidth) is carried through.
 */
export function migrateThemeSettings(raw) {
    const r = (raw ?? {});
    if (typeof r === 'object' && r !== null && (r.schemaVersion ?? 0) >= 2) {
        return raw; // already v2 — untouched
    }
    const shape = r.shape && typeof r.shape === 'object'
        ? { ...DEFAULT_THEME_SETTINGS.shape, ...r.shape }
        : DEFAULT_THEME_SETTINGS.shape;
    return {
        ...DEFAULT_THEME_SETTINGS,
        colors: { ...DEFAULT_THEME_SETTINGS.colors },
        typography: {
            heading: { ...DEFAULT_THEME_SETTINGS.typography.heading },
            body: { ...DEFAULT_THEME_SETTINGS.typography.body },
        },
        shape,
        // A v1 blob predates the favicon entirely — there is nothing to carry forward, only a fresh empty
        // object so the migrated theme never aliases the module-level default.
        brand: { ...DEFAULT_THEME_SETTINGS.brand },
        // A v1 blob predates the urgency chip too — a fresh copy of the defaults (off), never an alias.
        productBadges: { ...DEFAULT_PRODUCT_BADGES },
    };
}
//# sourceMappingURL=context.js.map